import React, { useEffect, useRef, useCallback, useContext } from 'react';
import { useAccessibility } from './useAccessibility';
import { trapFocus, saveFocus, restoreFocus, focusFirstElement } from './focus';
import { announce } from './announcements';
import { LangContext } from '../context/LangContext';
import {
  GlobeIcon, BookIcon, ContrastIcon, InvertIcon, MoonIcon, SunIcon,
  DropletIcon, PointerIcon, MinusIcon, MaskIcon, ImageIcon, LinkIcon,
  HeadingIcon, UnderlineIcon, SquareIcon, PauseIcon, StopIcon,
  CrosshairIcon, KeyboardIcon, A11yIcon, CloseIcon
} from './icons';

function ToggleSetting({ label, id, checked, onChange, icon: Icon }) {
  return (
    <div className="a11y-menu-item">
      <span className="a11y-menu-item-label">
        {Icon && <span className="a11y-menu-icon"><Icon /></span>}
        {label}
      </span>
      <button
        role="switch"
        id={id}
        aria-checked={checked}
        aria-label={label}
        className={`a11y-toggle ${checked ? 'a11y-toggle-active' : ''}`}
        onClick={() => onChange(!checked)}
        tabIndex={0}
      >
        <span className="a11y-toggle-track">
          <span className="a11y-toggle-thumb" />
        </span>
      </button>
    </div>
  );
}

function RangeSetting({ label, value, min, max, step, onChange, unit }) {
  return (
    <div className="a11y-menu-item">
      <span className="a11y-menu-item-label">{label}</span>
      <div className="a11y-range-control">
        <button
          className="a11y-range-btn"
          onClick={() => onChange(Math.max(min, value - step))}
          aria-label={`Decrease ${label}`}
          tabIndex={-1}
        >
          −
        </button>
        <span className="a11y-range-value" aria-live="polite">
          {value}{unit || '%'}
        </span>
        <button
          className="a11y-range-btn"
          onClick={() => onChange(Math.min(max, value + step))}
          aria-label={`Increase ${label}`}
          tabIndex={-1}
        >
          +
        </button>
      </div>
    </div>
  );
}

const SETTINGS_GROUPS = [
  {
    title: 'Text',
    items: [
      { type: 'range', label: 'Text Size', key: 'fontSize', min: 50, max: 200, step: 10, unit: '%' },
      { type: 'range', label: 'Letter Spacing', key: 'letterSpacing', min: 0, max: 10, step: 1, unit: 'px' },
      { type: 'range', label: 'Word Spacing', key: 'wordSpacing', min: 0, max: 20, step: 1, unit: 'px' },
      { type: 'range', label: 'Line Height', key: 'lineHeight', min: 1, max: 3, step: 0.25, unit: '' },
      { type: 'toggle', label: 'Dyslexia Friendly Font', key: 'dyslexiaFont', icon: BookIcon },
    ],
  },
  {
    title: 'Colors & Contrast',
    items: [
      { type: 'toggle', label: 'High Contrast', key: 'highContrast', icon: ContrastIcon },
      { type: 'toggle', label: 'Negative Contrast', key: 'negativeContrast', icon: InvertIcon },
      { type: 'toggle', label: 'Dark Mode', key: 'darkMode', icon: MoonIcon },
      { type: 'toggle', label: 'Light Mode', key: 'lightMode', icon: SunIcon },
      { type: 'toggle', label: 'Grayscale', key: 'grayscale', icon: DropletIcon },
      { type: 'range', label: 'Saturation', key: 'saturation', min: 0, max: 200, step: 10, unit: '%' },
      { type: 'range', label: 'Brightness', key: 'brightness', min: 50, max: 200, step: 10, unit: '%' },
    ],
  },
  {
    title: 'Visual Aids',
    items: [
      { type: 'toggle', label: 'Bigger Cursor', key: 'biggerCursor', icon: PointerIcon },
      { type: 'toggle', label: 'Reading Guide', key: 'readingGuide', icon: MinusIcon },
      { type: 'toggle', label: 'Reading Mask', key: 'readingMask', icon: MaskIcon },
      { type: 'toggle', label: 'Remove Images', key: 'removeImages', icon: ImageIcon },
      { type: 'toggle', label: 'Highlight Links', key: 'highlightLinks', icon: LinkIcon },
      { type: 'toggle', label: 'Highlight Headings', key: 'highlightHeadings', icon: HeadingIcon },
      { type: 'toggle', label: 'Underline Links', key: 'underlineLinks', icon: UnderlineIcon },
      { type: 'toggle', label: 'Highlight Buttons', key: 'highlightButtons', icon: SquareIcon },
    ],
  },
  {
    title: 'Motion & Sound',
    items: [
      { type: 'toggle', label: 'Pause Animations', key: 'pauseAnimations', icon: PauseIcon },
      { type: 'toggle', label: 'Stop Auto Scroll', key: 'stopAutoScroll', icon: StopIcon },
    ],
  },
  {
    title: 'Navigation',
    items: [
      { type: 'toggle', label: 'Focus Mode', key: 'focusMode', icon: CrosshairIcon },
      { type: 'toggle', label: 'Keyboard Nav Mode', key: 'keyboardNavMode', icon: KeyboardIcon },
      { type: 'toggle', label: 'Screen Reader Helper', key: 'screenReaderHelper', icon: A11yIcon },
    ],
  },
];

export default function AccessibilityMenu({ isOpen, onClose }) {
  const panelRef = useRef(null);
  const { settings, setSetting, resetSettings } = useAccessibility();
  const lang = useContext(LangContext);
  const isNepali = lang?.state?.np || false;
  const switchLang = () => {
    if (isNepali) {
      lang?.dispatch({ type: 'ENGLISH' });
      announce('Language switched to English');
    } else {
      lang?.dispatch({ type: 'NEPALI' });
      announce('Language switched to Nepali');
    }
  };

  useEffect(() => {
    if (isOpen && panelRef.current) {
      saveFocus();
      focusFirstElement(panelRef.current);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      restoreFocus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    function handleKeyDown(e) {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      trapFocus(panelRef.current, e);
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleReset = useCallback(() => {
    resetSettings();
    announce('All accessibility settings have been reset to default');
  }, [resetSettings]);

  if (!isOpen) return null;

  return (
    <div
      id="a11y-panel"
      ref={panelRef}
      className="a11y-panel"
      role="dialog"
      aria-modal="true"
      aria-label="Accessibility Settings"
    >
      <div className="a11y-panel-header">
        <h2 id="a11y-panel-title" className="a11y-panel-title">
          Accessibility
        </h2>
        <button
          className="a11y-panel-close"
          onClick={onClose}
          aria-label="Close accessibility panel"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClose(); } }}
        >
          <CloseIcon />
        </button>
      </div>

      <div className="a11y-menu-group">
        <h3 className="a11y-menu-group-title">Language</h3>
        <div className="a11y-menu-item">
          <span className="a11y-menu-item-label">
            <span className="a11y-menu-icon"><GlobeIcon /></span>
            {isNepali ? 'नेपाली' : 'English'}
          </span>
          <button
            role="switch"
            aria-checked={isNepali}
            aria-label="Toggle language between English and Nepali"
            className={`a11y-toggle ${isNepali ? 'a11y-toggle-active' : ''}`}
            onClick={switchLang}
            tabIndex={0}
          >
            <span className="a11y-toggle-track">
              <span className="a11y-toggle-thumb" />
            </span>
          </button>
        </div>
      </div>

      <div className="a11y-panel-body">
        {SETTINGS_GROUPS.map((group) => (
          <div key={group.title} className="a11y-menu-group">
            <h3 className="a11y-menu-group-title">{group.title}</h3>
            {group.items.map((item) => {
              if (item.type === 'toggle') {
                return (
                  <ToggleSetting
                    key={item.key}
                    label={item.label}
                    id={`a11y-${item.key}`}
                    checked={settings[item.key]}
                    onChange={(val) => setSetting(item.key, val)}
                    icon={item.icon}
                  />
                );
              }
              if (item.type === 'range') {
                return (
                  <RangeSetting
                    key={item.key}
                    label={item.label}
                    value={settings[item.key]}
                    min={item.min}
                    max={item.max}
                    step={item.step}
                    unit={item.unit}
                    onChange={(val) => setSetting(item.key, val)}
                  />
                );
              }
              return null;
            })}
          </div>
        ))}
      </div>

      <div className="a11y-panel-footer">
        <button
          className="a11y-reset-btn"
          onClick={handleReset}
          aria-label="Reset all accessibility settings"
          tabIndex={0}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleReset(); } }}
        >
          Reset All Settings
        </button>
        <p className="a11y-shortcuts-hint">
          Shortcuts: Alt+A (Open) · Esc (Close) · Alt+R (Reset)
        </p>
      </div>
    </div>
  );
}
